import React, { useEffect } from "react";
import { Card, Radio, Space, Button, Form, message, Progress } from "antd";
import { calculateScore } from "../utils/scoring";
import { CONSTANTS } from "../utils/constants";
import { useNavigate } from "react-router-dom";
import { BCL_VERBIAGE, CAT_VERBIAGE, SSC_ER_VERBIAGE, SSC_SD_VERBIAGE } from "../utils/verbiage";

const SinglePageTest = ({ test, testId, responses, setResponses }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    const saved = JSON.parse(sessionStorage.getItem(`${testId}-responses`) || "{}");
    setResponses(saved);
    form.setFieldsValue(saved);
  }, [testId]);

  useEffect(() => {
    sessionStorage.setItem(`${testId}-responses`, JSON.stringify(responses));
  }, [responses, testId]);

  // Count answered questions for progress
  const totalQuestions = test.questions.length;
  const answeredCount = Object.keys(responses).filter((k) => responses[k] > -1).length;
  const percent = Math.round((answeredCount / totalQuestions) * 100);

  const handleChange = (qIndex, value) => {
    setResponses((p) => ({ ...p, [qIndex]: value }));
    form.setFieldValue(qIndex, value);
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const { total } = calculateScore(testId, values);
      sessionStorage.setItem(`${testId}-responses`, JSON.stringify(values));
      sessionStorage.setItem(`${testId}-score`, total);
      setResponses(values);
      navigate(`/results`, {
        state: {
          test: test,
          testId: testId,
        }
      })
    } catch {
      message.error(CONSTANTS.ANSWERALLBEFORESUBMIT);
    }
  };

  const getVerbiage = () => {
    switch (testId) {
      case CONSTANTS.SSCERID:
        return SSC_ER_VERBIAGE;
      case CONSTANTS.SSCSDID:
        return SSC_SD_VERBIAGE;
      case CONSTANTS.BCLID:
        return BCL_VERBIAGE;
      case CONSTANTS.CATID:
        return CAT_VERBIAGE;
      default:
        break;
    }
  }

  return (
    <div className="px-3">
      <h2 className="text-2xl font-semibold my-4">{test.title}</h2>
      <div className="mb-4">
        {getVerbiage()}
      </div>
      <div className="mb-6">
        <span>
          <strong>Progress:</strong> {answeredCount} / {totalQuestions}
        </span>
        <Progress percent={percent} status={percent === 100 ? "success" : "active"} />
      </div>
      <div className="mb-6">
        <Form
          form={form}
          layout="vertical"
          initialValues={responses}
          onFinish={handleSubmit}
          scrollToFirstError
        >
          <Space direction="vertical" className="!w-full">
            {test.questions.map((q, i) => (
              <Card key={i} size="small" className="shadow-sm">
                <Form.Item
                  name={i}
                  label={
                    <div className="flex">
                      <span className="text-gray-500 min-w-9 text-center">{`Q${i + 1}) `}</span>                  
                      {testId === CONSTANTS.BCLID ? (
                        <span className="text-base">
                          <span className="font-semibold">{test.questionPrefix}</span>{" "}
                          <span
                            dangerouslySetInnerHTML={{
                              __html: q.text,
                            }}
                          />
                        </span>
                      ) : (
                        <span
                          className="text-base font-semibold"
                          dangerouslySetInnerHTML={{
                            __html: `${test.questionPrefix} ${q.text}`,
                          }}
                        />
                      )}
                    </div>
                  }                                   
                  rules={[{ required: true, message: CONSTANTS.SELECTANSWER }]}
                  className="!mb-0"
                >
                  <Radio.Group
                    onChange={(e) => handleChange(i, e.target.value)}
                    value={responses[i]}
                  >
                    <Space className="ml-11" direction="horizontal" wrap>
                      {test.options.map((opt, idx) => (
                        <Radio value={opt?.point} key={idx}>
                          {opt?.value}
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Card>
            ))}
          </Space>
        </Form>
        <div className="flex mt-4 justify-end">
          <Button type="primary" onClick={() => form.submit()}>
            {CONSTANTS.VIEWRESULT}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SinglePageTest;